import { HostBinding, inject } from '@angular/core';
import { AppVisualContextService } from 'src/app/shared/services/app-visual-context.service';
import { AppAccent } from 'src/app/core/types/app-accent.type';
import { Component, ElementRef, HostListener, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { FormHelper } from 'src/app/core/helpers/form.helper';
import { AuthService, ServicoExterno, UsuarioExterno, UsuarioExternoResponse } from 'src/app/shared/services/auth.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { PopupService } from 'src/app/shared/services/popup.service';

/**
 * Área do usuário externo (ex.: defensor vinculado a um PAD). O conteúdo específico do PAD ainda será
 * desenvolvido; por enquanto expõe identidade (dados básicos), troca de senha e logoff.
 */
@Component({
  selector: 'app-externo',
  templateUrl: './externo.component.html',
  styleUrls: ['./externo.component.css'],
})
export class ExternoComponent implements OnInit {
  private readonly visualContext = inject(AppVisualContextService);
  @HostBinding('class') get visualClass(): string {
    return this.accent === undefined ? '' : this.visualContext.classForAccent(this.accent);
  }

  @ViewChild('modalAlterarSenha') modalAlterarSenha: TemplateRef<any>;

  accent: AppAccent = 'solicitacao';
  usuarioExterno: UsuarioExterno | null = null;
  servicosExterno: ServicoExterno[] = [];
  menuAberto: boolean = false;

  hideSenhaAtual: boolean = true;
  hideSenhaNova: boolean = true;
  hideConfirmarSenha: boolean = true;
  formSenha: FormGroup;

  constructor(
    private authService: AuthService,
    private elementRef: ElementRef,
    private formBuilder: FormBuilder,
    private modalService: ModalService,
    private popupService: PopupService,
    private router: Router
  ) {}

  /** Fecha o menu do usuário ao clicar fora dele. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.menuAberto) return;
    const cliqueDentroDoMenu = this.elementRef.nativeElement.querySelector('.user-menu-wrapper')?.contains(event.target as Node);
    if (!cliqueDentroDoMenu) this.fecharMenu();
  }

  ngOnInit(): void {
    this.formSenha = this.formBuilder.group({
      senhaAtual: ['', [Validators.required]],
      senha: ['', [Validators.required, Validators.minLength(6)]],
      confirmarSenha: ['', [Validators.required, FormHelper.senhasConferemValidator()]],
    });

    if (!this.authService.tokenExterno) {
      this.sessaoExternaInvalida();
      return;
    }
    this.carregaMeusDados();
    this.carregaServicos();
  }

  toggleMenu(): void {
    this.menuAberto = !this.menuAberto;
  }

  fecharMenu(): void {
    this.menuAberto = false;
  }

  /** Evita repetir o título como subtítulo (ex.: serviços cuja DESCRICAO é idêntica ao SERVICO). */
  subtituloServico(servico: ServicoExterno): string {
    const descricao = servico.DESCRICAO?.trim() ?? '';
    return descricao && descricao.toUpperCase() !== servico.SERVICO?.trim().toUpperCase() ? descricao : '';
  }

  togglePasswordAtual(): void {
    this.hideSenhaAtual = !this.hideSenhaAtual;
  }

  togglePasswordNova(): void {
    this.hideSenhaNova = !this.hideSenhaNova;
  }

  toggleConfirmPassword(): void {
    this.hideConfirmarSenha = !this.hideConfirmarSenha;
  }

  abrirModalAlterarSenha(): void {
    this.fecharMenu();
    this.formSenha.reset();
    this.modalService
      .openModalSmall(this.modalAlterarSenha)
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: boolean) => {
        if (result) this.salvarSenha();
      });
  }

  sair(): void {
    // Best-effort: invalida o token no servidor, mas não bloqueia o logoff local por isso.
    this.authService.logoutExterno({ spinnerOff: true }).pipe(take(1)).subscribe();
    this.finalizaLogout();
  }

  private carregaMeusDados(): void {
    this.authService
      .meusDadosExterno({ spinnerDelay: 300 })
      .pipe(take(1))
      .subscribe({
        next: response => {
          if (response.error || !response.data) {
            this.sessaoExternaInvalida();
            return;
          }
          this.usuarioExterno = response.data;
        },
        error: () => this.sessaoExternaInvalida(),
      });
  }

  private carregaServicos(): void {
    this.authService
      .buscaServicosExterno({ spinnerDelay: 300 })
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.servicosExterno = response.error ? [] : response.data ?? [];
        },
        error: () => (this.servicosExterno = []),
      });
  }

  private salvarSenha(): void {
    const { senhaAtual, senha } = this.formSenha.value;
    const body = JSON.stringify({ senhaatual: senhaAtual, senha });

    this.authService
      .alterarSenhaExterno({ body, spinnerDelay: 300 })
      .pipe(take(1))
      .subscribe({
        next: response => {
          if (response.error) {
            this.popupService.showError(response.message || 'Falha ao alterar senha!');
            return;
          }
          this.popupService.showSuccess('Senha alterada com sucesso!');
        },
        error: (response: { error?: UsuarioExternoResponse }) => {
          this.popupService.showError(response?.error?.message || 'Falha ao alterar senha!');
        },
      });
  }

  private sessaoExternaInvalida(): void {
    this.finalizaLogout('Sua sessão expirou. Faça login novamente.');
  }

  private finalizaLogout(mensagem?: string): void {
    this.authService.limparSessaoExterna();
    if (mensagem) this.popupService.showError(mensagem);
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }
}
